import { useEffect, useState } from "react";
import { X, Trash2, ShoppingCart, LogIn, Plus, Minus, ExternalLink } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useCfxAuth } from "@/context/CfxAuthContext";
import { tebexCheckout } from "@/api";
import { toast } from "sonner";

function applyDiscount(priceStr, pct) {
  if (!priceStr || !pct || pct <= 0 || pct >= 100) return null;
  let touched = false;
  const out = priceStr.replace(/(\d+(?:\.\d+)?)/g, (m) => {
    touched = true;
    const n = parseFloat(m);
    const v = n * (1 - pct / 100);
    return m.includes(".") ? v.toFixed(2) : String(Math.round(v));
  });
  return touched ? out : null;
}

export default function CartDrawer() {
  const { items, open, setOpen, remove, updateQty, clear, count } = useCart();
  const { user, login } = useCfxAuth();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const handleCheckout = async () => {
    if (!items.length) return;
    if (!user) {
      toast.error("Please log in with Cfx.re to continue");
      login();
      return;
    }
    setSubmitting(true);
    try {
      const payload = items.map((i) => ({ product_id: i.product_id, quantity: i.quantity }));
      const data = await tebexCheckout(payload);
      // Tebex FiveM stores require an extra one-time auth step where the customer
      // links the Tebex basket to their FiveM account. Our backend returns an
      // `auth_url` for that flow. Universal stores just return `checkout_url`.
      const next = data.auth_url || data.checkout_url;
      if (next) {
        toast.success(data.auth_url ? "Linking your FiveM account on Tebex..." : "Redirecting to Tebex checkout...");
        clear();
        window.location.href = next;
      } else {
        toast.error("Tebex did not return a redirect URL");
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Checkout failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="cart-drawer"
      className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-md"
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
    >
      <aside
        className="absolute right-0 top-0 h-full w-full sm:w-[440px] md:w-[480px] bg-[#0a0a0a] border-l border-[var(--accent-dim)] flex flex-col"
        style={{
          backgroundImage: "radial-gradient(circle, rgba(var(--theme-accent-primary-rgb),0.04) 1px, transparent 1.5px)",
          backgroundSize: "16px 16px",
        }}
      >
        {/* HEADER */}
        <div className="p-5 border-b border-[var(--accent-dim)] flex items-center justify-between bg-black/60 backdrop-blur-md">
          <div>
            <div className="bracket">// CART_BUFFER</div>
            <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight text-glow flex items-center gap-3">
              <ShoppingCart size={22} className="text-[var(--accent)]" />
              CART
              <span
                data-testid="cart-count"
                className="font-mono text-sm bg-[var(--accent)] text-black px-2.5 py-0.5 rounded-full"
              >
                {count}
              </span>
            </h2>
          </div>
          <button
            data-testid="cart-close"
            onClick={() => setOpen(false)}
            className="w-10 h-10 flex items-center justify-center rounded-full border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-black transition"
            aria-label="Close cart"
          >
            <X size={18} />
          </button>
        </div>

        {/* USER STATUS */}
        <div className="px-5 py-3 border-b border-[var(--line)] bg-black/60">
          {user ? (
            <div className="flex items-center gap-3">
              {user.cfx_avatar_url ? (
                <img src={user.cfx_avatar_url} alt="" className="w-8 h-8 border border-[var(--accent)]/50" />
              ) : (
                <div className="w-8 h-8 border border-[var(--accent)]/50 bg-black flex items-center justify-center font-mono text-[10px] text-[var(--accent)]">
                  {user.cfx_username?.[0]?.toUpperCase()}
                </div>
              )}
              <div className="font-mono text-xs">
                <div className="text-[var(--accent)] tracking-widest">// CFX_LOGGED_IN</div>
                <div className="text-white">{user.cfx_username}</div>
              </div>
            </div>
          ) : (
            <button
              data-testid="cart-cfx-login"
              onClick={login}
              className="w-full btn-mega !py-3 !text-xs"
            >
              <LogIn size={12} /> LOGIN WITH CFX.RE TO CHECKOUT
            </button>
          )}
        </div>

        {/* ITEMS */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {items.length === 0 ? (
            <div className="text-center py-20 font-mono text-xs text-[var(--muted)] uppercase tracking-widest">
              // buffer empty
              <div className="mt-2 text-[10px]">add a product to begin transmission</div>
            </div>
          ) : (
            items.map((item) => {
              const dp = applyDiscount(item.price, item.discount);
              return (
                <div
                  key={item.product_id}
                  data-testid={`cart-item-${item.product_id}`}
                  className="border border-[var(--theme-border)] bg-[var(--theme-surface)]/60 rounded-xl p-3 flex gap-3"
                >
                  {item.image ? (
                    <img
                      src={item.image}
                      alt=""
                      className="w-20 h-20 object-cover rounded-lg border border-[var(--line)] bg-black"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = "/logo.png?v=4";
                        e.currentTarget.classList.add("object-contain", "p-2");
                      }}
                    />
                  ) : (
                    <img
                      src="/logo.png?v=4"
                      alt=""
                      className="w-20 h-20 object-contain p-2 rounded-lg border border-[var(--line)] bg-black opacity-90"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold uppercase text-sm leading-tight truncate">
                      {item.title}
                    </div>
                    {item.price && (
                      <div className="font-mono text-xs mt-1">
                        {dp ? (
                          <>
                            <span className="sale-strike mr-2">{item.price}</span>
                            <span className="text-[var(--accent)] font-bold sale-glow">{dp}</span>
                          </>
                        ) : (
                          <span className="text-[var(--accent)] font-bold">{item.price}</span>
                        )}
                      </div>
                    )}
                    <div className="mt-2 flex items-center gap-1">
                      <button
                        onClick={() => updateQty(item.product_id, item.quantity - 1)}
                        className="rounded-md border border-[var(--line-strong)] hover:border-[var(--accent)] w-6 h-6 flex items-center justify-center"
                        aria-label="decrease"
                      >
                        <Minus size={10} />
                      </button>
                      <input
                        data-testid={`cart-qty-${item.product_id}`}
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateQty(item.product_id, e.target.value)}
                        className="w-12 bg-black border border-[var(--line-strong)] text-center font-mono text-xs py-1"
                      />
                      <button
                        onClick={() => updateQty(item.product_id, item.quantity + 1)}
                        className="rounded-md border border-[var(--line-strong)] hover:border-[var(--accent)] w-6 h-6 flex items-center justify-center"
                        aria-label="increase"
                      >
                        <Plus size={10} />
                      </button>
                      <button
                        data-testid={`cart-remove-${item.product_id}`}
                        onClick={() => remove(item.product_id)}
                        className="ml-auto rounded-md border border-[var(--line-strong)] hover:border-red-500 hover:text-red-500 w-6 h-6 flex items-center justify-center"
                        aria-label="remove"
                      >
                        <Trash2 size={10} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* FOOTER */}
        <div className="p-5 border-t border-[var(--accent-dim)] bg-black/80 space-y-3">
          {items.length > 0 && (
            <button
              onClick={clear}
              className="w-full font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] hover:text-red-500 transition"
            >
              // clear buffer
            </button>
          )}
          <button
            data-testid="cart-checkout-btn"
            onClick={handleCheckout}
            disabled={submitting || items.length === 0}
            className="w-full btn-mega !py-4 !text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>// SUBMITTING_TO_TEBEX</>
            ) : items.length === 0 ? (
              <>// CART EMPTY</>
            ) : (
              <>
                <ExternalLink size={12} /> CHECKOUT VIA TEBEX
              </>
            )}
          </button>
          <p className="font-mono text-[9px] text-[var(--muted)] text-center uppercase tracking-widest leading-relaxed">
            // payment processed by tebex / assets locked to your cfx account
          </p>
        </div>
      </aside>
    </div>
  );
}
